import type { ValuedTemplateFeatures, ValueForReferenceFields } from "@scayla/api-client-fetch";
import {
  AvatarType,
  BuiltinModuleTag,
  getTemplateFeaturesForRecord,
  SystemTemplateSlug,
} from "@scayla/api-client-fetch";
import type { DetailsTooltipContent, DetailsTooltipContentItem } from "@scayla/components";
import { DetailsTooltipContentItemType } from "@scayla/components";
import { getCountryNames } from "@scayla/core";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { useEmailComposeDraft } from "@/composables/useEmailComposeDraft";
import { Routes } from "@/router";
import { useTemplateStore } from "@/store/templateStore";
import { useWorkspace } from "@/store/workspace";
import { ADDRESS_SUBFIELD_SLUGS } from "@/utils/address";
import { getModuleIcon } from "@/utils/moduleUtils";

export const useTooltipStore = defineStore("tooltip", () => {
  const currentShowToken = ref<number>(0);

  const visible = ref(false);
  const router = useRouter();
  const workspace = useWorkspace();
  const templateStore = useTemplateStore();
  const { locale } = useI18n();
  const { canComposeEmailDraft, getEmailHref, navigateToEmailDraft } = useEmailComposeDraft();

  const defaultContent: DetailsTooltipContent = {
    title: "",
    items: [],
    avatarUrl: undefined,
    avatarColorIdx: undefined,
    defaultModuleIcon: undefined,
  };
  const content = ref<DetailsTooltipContent>(defaultContent);
  const recordId = ref<string | null>(null);
  const moduleId = ref<string | null>(null);
  const templateId = ref<string | null>(null);
  const avatarType = ref<"initials" | "special">("initials");

  const recordUrl = computed(() => {
    if (!recordId.value || !moduleId.value) return undefined;
    return router.resolve({
      name: Routes.RECORD,
      params: {
        moduleId: moduleId.value,
        recordId: recordId.value,
      },
    }).href;
  });

  const targetRect = ref<DOMRect | null>(null);
  const placement = ref<"bottom-start" | string>("bottom-start");
  const loading = ref(false);
  const hideTimeout = ref<NodeJS.Timeout | null>(null);
  const isHoveredOnPopover = ref(false);
  const instant = ref(false);

  const countryNames = computed(() => getCountryNames(locale.value));

  const getCountryName = (countryCode: string): string => {
    return countryNames.value[countryCode] ?? countryCode;
  };

  // Slug format is "{groupSlug}/{subfieldSlug}" for any address group (built-in `addresses` or
  // custom addressList fields with an arbitrary group slug).
  function parseAddressSlug(slug: string): { groupSlug: string; subfieldSlug: string } | null {
    const slashIndex = slug.indexOf("/");
    if (slashIndex === -1) return null;
    const groupSlug = slug.slice(0, slashIndex);
    const subfieldSlug = slug.slice(slashIndex + 1);
    if (!ADDRESS_SUBFIELD_SLUGS.has(subfieldSlug)) return null;
    return { groupSlug, subfieldSlug };
  }

  // --------------------
  // Actions
  // --------------------

  // Layout reflow right after navigating can leave the cursor genuinely (if slightly) moving over
  // stale content, so suppress new previews for a short window after any route change.
  const ROUTE_CHANGE_SUPPRESS_MS = 1500;
  let suppressUntil = 0;

  const show = async (options: { recordId?: string; target: HTMLElement; placement?: string }) => {
    if (Date.now() < suppressUntil) return;

    // Clear any leftover instant-hide flag from a previous route change before this cycle's own hide.
    instant.value = false;

    const showToken = ++currentShowToken.value;
    if (hideTimeout.value) clearTimeout(hideTimeout.value);

    recordId.value = options.recordId ?? null;
    targetRect.value = options.target.getBoundingClientRect();
    placement.value = options.placement ?? "bottom-start";

    content.value = defaultContent;
    avatarType.value = "initials";

    visible.value = false;
    loading.value = true;

    const canShow = await prepareTooltipContent();
    if (!canShow) {
      return;
    }

    if (showToken !== currentShowToken.value) return;

    loading.value = false;
    visible.value = true;
  };

  const hide = () => {
    if (hideTimeout.value) clearTimeout(hideTimeout.value);

    hideTimeout.value = setTimeout(() => {
      if (!isHoveredOnPopover.value) {
        visible.value = false;
        content.value = defaultContent;
        targetRect.value = null;
        isHoveredOnPopover.value = false;
        currentShowToken.value++;
      }
    }, 200);
  };

  // hide()'s 200ms delay can be cancelled by a stray hover on the popover itself (its position
  // doesn't move just because the route did) — use this where that must not happen.
  const hideImmediately = () => {
    if (hideTimeout.value) clearTimeout(hideTimeout.value);
    hideTimeout.value = null;
    visible.value = false;
    content.value = defaultContent;
    targetRect.value = null;
    isHoveredOnPopover.value = false;
    currentShowToken.value++;
  };

  // Any route change means whatever this preview was anchored to is no longer the current view.
  watch(
    () => router.currentRoute.value.fullPath,
    () => {
      suppressUntil = Date.now() + ROUTE_CHANGE_SUPPRESS_MS;
      instant.value = true;
      hideImmediately();
    },
  );

  const setHoveredOnPopover = (hovered: boolean) => {
    isHoveredOnPopover.value = hovered;
    if (!hovered) {
      hide();
    } else {
      if (hideTimeout.value) clearTimeout(hideTimeout.value);
    }
  };

  // --------------------
  // Content preparation
  // --------------------

  const prepareTooltipContent = async (): Promise<boolean> => {
    if (!recordId.value) {
      content.value.items = defaultContent.items;
      return false;
    }

    loading.value = true;
    const result = await getTemplateFeaturesForRecord({
      path: {
        recordId: recordId.value,
        featureType: "tooltip",
      },
    });

    const data = result.data;

    if (result.error || !data) {
      return false;
    }

    // Prepare main content
    content.value.title = data.name;
    content.value.avatarUrl = data.avatarUrl;
    content.value.avatarColorIdx = data.avatarColorIdx;
    content.value.isPerson =
      (data.templateSlug as SystemTemplateSlug) === SystemTemplateSlug.SYSTEM_TEMPLATE_SLUG_CONTACT_PERSON;
    moduleId.value = data.moduleId;
    templateId.value = data.templateId;
    const moduleTag = workspace.modules?.find((module) => module.id === data.moduleId)?.builtinModule;
    const defaultModuleIcon = getModuleIcon(moduleTag);
    avatarType.value = "initials";
    content.value.defaultModuleIcon = defaultModuleIcon;
    if (moduleTag === BuiltinModuleTag.DOCUMENTS && templateId.value) {
      const template = await templateStore.getTemplate(templateId.value);
      if (template?.avatarType === AvatarType.SPEC_DOSSIER) {
        avatarType.value = "special";
        content.value.specialIcon = "generic/dossier";
      }
    }

    // Prepare features
    const features = [...data.features].sort((a, b) => a.orderIndex - b.orderIndex);

    const addressLinesByGroup = buildAddressLinesByGroup(features);

    const items: DetailsTooltipContentItem[] = [];
    const addedAddressGroups = new Set<string>();

    for (const feature of features) {
      const slug = feature.fieldSlug;
      const value = feature.value;

      if (!value) continue;

      let displayValue: string;
      if (typeof value === "string") {
        displayValue = value;
      } else {
        displayValue = value.name;
      }

      const parsed = parseAddressSlug(slug);
      if (parsed) {
        if (!addedAddressGroups.has(parsed.groupSlug)) {
          addedAddressGroups.add(parsed.groupSlug);
          const lines = addressLinesByGroup.get(parsed.groupSlug) ?? [];
          if (lines.length > 0) {
            items.push({
              type: DetailsTooltipContentItemType.ADDRESS,
              text: lines,
            });
          }
        }
        continue;
      }

      if (slug === "phone") {
        items.push({
          type: DetailsTooltipContentItemType.PHONE,
          text: displayValue,
        });
      } else if (slug === "email") {
        items.push({
          type: DetailsTooltipContentItemType.EMAIL,
          text: displayValue,
          href: getEmailHref(displayValue),
          action: canComposeEmailDraft.value
            ? () => {
                void navigateToEmailDraft(displayValue).then(() => hideImmediately());
              }
            : undefined,
        });
      } else if (slug === "_template") {
        items.push({
          type: DetailsTooltipContentItemType.TEMPLATE,
          text: displayValue,
          action: createNavigateToTemplateAction(),
        });
      } else if (slug === "parent_record") {
        items.push({
          type: DetailsTooltipContentItemType.PARENT,
          text: displayValue,
          action: createNavigateAction(value),
        });
      } else {
        if (displayValue.trim()) {
          items.push({
            type: DetailsTooltipContentItemType.OTHER,
            text: displayValue,
            action: createNavigateAction(value),
          });
        }
      }
    }

    content.value.items = items;

    return true;
  };

  const buildAddressLinesByGroup = (features: ValuedTemplateFeatures[]): Map<string, string[]> => {
    // Group address subfields by their owning address group first — a record's tooltip may show
    // subfields from more than one address group (built-in `addresses` and/or custom addressList
    // fields), and each must combine into its own separate Maps link.
    const componentsByGroup = new Map<string, { orderIndex: number; fieldName: string; displayValue: string }[]>();

    for (const feature of features) {
      const slug = feature.fieldSlug;
      const value = feature.value;

      const parsed = parseAddressSlug(slug);
      if (!value || !parsed) continue;

      let displayValue: string;
      if (typeof value === "string") {
        displayValue = value;
      } else {
        continue;
      }

      const fieldName = parsed.subfieldSlug;
      const addressValue = fieldName === "country" ? getCountryName(value) : displayValue;

      if (!componentsByGroup.has(parsed.groupSlug)) componentsByGroup.set(parsed.groupSlug, []);
      componentsByGroup.get(parsed.groupSlug)!.push({
        orderIndex: feature.orderIndex,
        fieldName,
        displayValue: addressValue,
      });
    }

    const result = new Map<string, string[]>();
    for (const [groupSlug, addressComponents] of componentsByGroup) {
      result.set(groupSlug, combineAddressComponents(addressComponents));
    }
    return result;
  };

  // Extracted as-is from the previous single-group buildAddressLines — combines one group's
  // components (streethouse, address lines, zipplace, country) into display lines.
  function combineAddressComponents(
    addressComponents: { orderIndex: number; fieldName: string; displayValue: string }[],
  ): string[] {
    const addressLines: string[] = [];

    // Find indices for components
    let streetIdx = -1;
    let houseIdx = -1;
    let addressLine1Idx = -1;
    let addressLine2Idx = -1;
    let cityIdx = -1;
    let zipIdx = -1;
    let countryIdx = -1;

    for (let i = 0; i < addressComponents.length; i++) {
      const comp = addressComponents[i];
      if (comp.fieldName === "street") {
        streetIdx = i;
      } else if (comp.fieldName === "house") {
        houseIdx = i;
      } else if (comp.fieldName === "address_line_1") {
        addressLine1Idx = i;
      } else if (comp.fieldName === "address_line_2") {
        addressLine2Idx = i;
      } else if (comp.fieldName === "place") {
        cityIdx = i;
      } else if (comp.fieldName === "zip") {
        zipIdx = i;
      } else if (comp.fieldName === "country") {
        countryIdx = i;
      }
    }

    const processedIndices = new Set<number>();

    // Combine street and house
    const hasStreet = streetIdx !== -1;
    const hasHouse = houseIdx !== -1;
    if (hasStreet || hasHouse) {
      const streetValue = hasStreet ? addressComponents[streetIdx].displayValue : "";
      const houseValue = hasHouse ? addressComponents[houseIdx].displayValue : "";
      const combined = [streetValue, houseValue].filter(Boolean).join(" ");
      if (combined.trim()) {
        addressLines.push(combined);
      }
      if (hasStreet) processedIndices.add(streetIdx);
      if (hasHouse) processedIndices.add(houseIdx);
    }

    // Add Additional 1
    if (addressLine1Idx !== -1) {
      const addressLine1Value = addressComponents[addressLine1Idx].displayValue;
      if (addressLine1Value.trim()) {
        addressLines.push(addressLine1Value);
      }
      processedIndices.add(addressLine1Idx);
    }

    // Add Additional 2
    if (addressLine2Idx !== -1) {
      const addressLine2Value = addressComponents[addressLine2Idx].displayValue;
      if (addressLine2Value.trim()) {
        addressLines.push(addressLine2Value);
      }
      processedIndices.add(addressLine2Idx);
    }

    // Combine zip and place
    const hasCity = cityIdx !== -1;
    const hasZip = zipIdx !== -1;
    if (hasCity || hasZip) {
      const cityValue = hasCity ? addressComponents[cityIdx].displayValue : "";
      const zipValue = hasZip ? addressComponents[zipIdx].displayValue : "";
      const combined = [zipValue, cityValue].filter(Boolean).join(" ");
      if (combined.trim()) {
        addressLines.push(combined);
      }
      if (hasCity) processedIndices.add(cityIdx);
      if (hasZip) processedIndices.add(zipIdx);
    }

    // Mark country as processed but don't add it yet
    if (countryIdx !== -1) {
      processedIndices.add(countryIdx);
    }

    // Add any remaining address components that weren't processed
    for (let i = 0; i < addressComponents.length; i++) {
      if (!processedIndices.has(i)) {
        const comp = addressComponents[i];
        if (comp.displayValue.trim()) {
          addressLines.push(comp.displayValue);
        }
      }
    }

    // Add country at the end
    if (countryIdx !== -1) {
      const countryValue = addressComponents[countryIdx].displayValue;
      if (countryValue.trim()) {
        addressLines.push(countryValue);
      }
    }

    return addressLines;
  }

  // --------------------
  // Navigation helpers
  // --------------------

  const createNavigateToTemplateAction = () => {
    // checking template read permission first
    if (!workspace.getTemplatePermissions(templateId.value).read) {
      return undefined;
    }

    return () => {
      void router.push({
        name: Routes.MODULE,
        params: {
          moduleId: moduleId.value,
        },
        query: {
          templateId: templateId.value,
        },
      });
      hideImmediately();
    };
  };

  const createNavigateAction = (value: string | ValueForReferenceFields) => {
    return () => {
      if (typeof value === "string") return;

      if (!value.moduleId || !value.recordId) {
        return;
      }

      void navigateToRecord(value.moduleId, value.recordId);
    };
  };

  const navigateToCurrentRecord = async () => {
    if (!moduleId.value || !recordId.value) return;
    await navigateToRecord(moduleId.value, recordId.value);
  };

  const navigateToRecord = async (moduleId: string, recordId: string) => {
    await router.push({
      name: Routes.RECORD,
      params: {
        moduleId,
        recordId,
      },
    });
    hideImmediately();
  };

  return {
    visible,
    content,
    targetRect,
    placement,
    loading,
    avatarType,
    instant,
    show,
    hide,
    setHoveredOnPopover,
    navigateToCurrentRecord,
    recordUrl,
  };
});
